/**
 * src/shell/identity/AvatarPhotoEditor.tsx
 *
 * Secure in-browser circular photo crop and scale editor.
 * Fully keyboard-accessible with arrow keys, zoom/rotate sliders, ARIA labels,
 * and preset avatar library integration.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';

export interface AvatarPhotoEditorProps {
  readonly currentAvatarUrl?: string;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSave: (dataUrl: string) => void;
}

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB
const OUTPUT_SIZE = 96; // 96x96 px output
const CANVAS_PREVIEW_SIZE = 240; // 240x240 px editor canvas

export const PRESET_AVATARS = [
  { id: 'bot-1', name: 'Cyber Bot Alpha', icon: '🤖', url: 'https://api.dicebear.com/7.x/bottts/svg?seed=alpha' },
  { id: 'bot-2', name: 'Mesh Node Sentry', icon: '🛡️', url: 'https://api.dicebear.com/7.x/bottts/svg?seed=sentry' },
  { id: 'operator', name: 'Core Operator', icon: '⚡', url: 'https://api.dicebear.com/7.x/bottts/svg?seed=operator' },
  { id: 'hacker', name: 'Cryptographic Agent', icon: '👾', url: 'https://api.dicebear.com/7.x/bottts/svg?seed=crypto_agent' },
];

export const AvatarPhotoEditor: React.FC<AvatarPhotoEditorProps> = ({
  currentAvatarUrl,
  isOpen,
  onClose,
  onSave,
}) => {
  const [activeTab, setActiveTab] = useState<'CROP' | 'PRESETS'>('CROP');
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [zoom, setZoom] = useState<number>(1.0);
  const [rotation, setRotation] = useState<number>(0);
  const [offset, setOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [error, setError] = useState<string | null>(null);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setImageSrc(currentAvatarUrl || null);
      setZoom(1.0);
      setRotation(0);
      setOffset({ x: 0, y: 0 });
      setError(null);
    }
  }, [isOpen, currentAvatarUrl]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setError('File is too large. Maximum size is 5 MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target?.result as string;
      setImageSrc(result);
    };
    reader.readAsDataURL(file);
  };

  const drawPreview = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, CANVAS_PREVIEW_SIZE, CANVAS_PREVIEW_SIZE);

    if (imgRef.current && imgRef.current.complete) {
      const img = imgRef.current;
      ctx.save();
      ctx.translate(CANVAS_PREVIEW_SIZE / 2 + offset.x, CANVAS_PREVIEW_SIZE / 2 + offset.y);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.scale(zoom, zoom);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);
      ctx.restore();
    }

    // Draw circular mask overlay
    ctx.save();
    ctx.strokeStyle = '#38bdf8';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(CANVAS_PREVIEW_SIZE / 2, CANVAS_PREVIEW_SIZE / 2, CANVAS_PREVIEW_SIZE / 2 - 8, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }, [offset, rotation, zoom]);

  useEffect(() => {
    if (imageSrc) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        imgRef.current = img;
        drawPreview();
      };
      img.src = imageSrc;
    }
  }, [imageSrc, drawPreview]);

  // Keyboard accessibility for nudge controls
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const step = e.shiftKey ? 15 : 5;
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setOffset((o) => ({ ...o, y: o.y - step }));
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOffset((o) => ({ ...o, y: o.y + step }));
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      setOffset((o) => ({ ...o, x: o.x - step }));
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      setOffset((o) => ({ ...o, x: o.x + step }));
    } else if (e.key === '+' || e.key === '=') {
      e.preventDefault();
      setZoom((z) => Math.min(3.0, z + 0.1));
    } else if (e.key === '-') {
      e.preventDefault();
      setZoom((z) => Math.max(0.5, z - 0.1));
    }
  };

  const handleSave = () => {
    if (!imageSrc) return;
    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = OUTPUT_SIZE;
    exportCanvas.height = OUTPUT_SIZE;
    const ctx = exportCanvas.getContext('2d');
    if (!ctx) return;

    if (imgRef.current) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, 0, Math.PI * 2);
      ctx.clip();
      ctx.translate(OUTPUT_SIZE / 2, OUTPUT_SIZE / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.scale(zoom * (OUTPUT_SIZE / CANVAS_PREVIEW_SIZE), zoom * (OUTPUT_SIZE / CANVAS_PREVIEW_SIZE));
      ctx.drawImage(imgRef.current, -imgRef.current.width / 2, -imgRef.current.height / 2);
      ctx.restore();
    }

    const dataUrl = exportCanvas.toDataURL('image/jpeg', 0.85);
    onSave(dataUrl);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Avatar Photo Cropper"
      onKeyDown={handleKeyDown}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 animate-in fade-in duration-150"
    >
      <div className="w-full max-w-sm bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl p-6 text-slate-100 flex flex-col items-center">
        <div className="flex w-full justify-between items-center mb-4 pb-2 border-b border-slate-800">
          <h3 className="text-sm font-bold text-white font-mono">AVATAR PHOTO EDITOR</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1 rounded">✕</button>
        </div>

        {/* Tab selection */}
        <div className="flex gap-2 w-full mb-4 bg-slate-950 p-1 rounded-lg border border-slate-800">
          <button
            type="button"
            onClick={() => setActiveTab('CROP')}
            className={`flex-1 py-1 text-xs font-mono rounded ${activeTab === 'CROP' ? 'bg-cyan-600 text-white font-bold' : 'text-slate-400 hover:text-slate-200'}`}
          >
            Crop & Adjust
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('PRESETS')}
            className={`flex-1 py-1 text-xs font-mono rounded ${activeTab === 'PRESETS' ? 'bg-cyan-600 text-white font-bold' : 'text-slate-400 hover:text-slate-200'}`}
          >
            Preset Library
          </button>
        </div>

        {activeTab === 'CROP' ? (
          <div className="flex flex-col items-center w-full space-y-3">
            <div
              tabIndex={0}
              aria-label="Crop Area. Use Arrow keys to move, +/- to zoom."
              className="p-1.5 bg-slate-950 border border-slate-800 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:outline-none"
            >
              <canvas
                ref={canvasRef}
                width={CANVAS_PREVIEW_SIZE}
                height={CANVAS_PREVIEW_SIZE}
                className="bg-slate-950 rounded-lg cursor-grab"
              />
            </div>

            <p className="text-[10px] text-slate-400 font-mono text-center">
              Use <kbd className="bg-slate-800 px-1 py-0.5 rounded">↑</kbd> <kbd className="bg-slate-800 px-1 py-0.5 rounded">↓</kbd> <kbd className="bg-slate-800 px-1 py-0.5 rounded">←</kbd> <kbd className="bg-slate-800 px-1 py-0.5 rounded">→</kbd> to nudge offset.
            </p>

            {/* Sliders for Zoom & Rotate */}
            <div className="w-full space-y-2 pt-2">
              <div className="flex items-center justify-between text-xs font-mono text-slate-300">
                <label htmlFor="avatar-zoom">Zoom</label>
                <input
                  id="avatar-zoom"
                  type="range"
                  min="0.5"
                  max="3.0"
                  step="0.1"
                  value={zoom}
                  onChange={(e) => setZoom(parseFloat(e.target.value))}
                  className="w-40"
                  aria-label="Zoom scale"
                />
              </div>

              <div className="flex items-center justify-between text-xs font-mono text-slate-300">
                <label htmlFor="avatar-rotate">Rotate</label>
                <input
                  id="avatar-rotate"
                  type="range"
                  min="0"
                  max="360"
                  step="5"
                  value={rotation}
                  onChange={(e) => setRotation(parseInt(e.target.value, 10))}
                  className="w-40"
                  aria-label="Rotation degrees"
                />
              </div>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/png, image/jpeg, image/webp"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 w-full my-2">
            {PRESET_AVATARS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                onClick={() => {
                  setImageSrc(preset.url);
                  setActiveTab('CROP');
                }}
                className="flex flex-col items-center p-3 bg-slate-950 hover:bg-slate-800 border border-slate-800 rounded-xl transition-all"
              >
                <span className="text-3xl mb-1">{preset.icon}</span>
                <span className="text-[11px] font-mono text-slate-200">{preset.name}</span>
              </button>
            ))}
          </div>
        )}

        {error && (
          <p className="text-xs font-mono text-amber-400 bg-amber-950/40 p-2 rounded border border-amber-900/60 mt-2 w-full text-center">
            {error}
          </p>
        )}

        <div className="flex gap-2 w-full mt-4 pt-3 border-t border-slate-800">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex-1 py-2 bg-slate-800 hover:bg-slate-700 text-xs font-mono rounded-lg border border-slate-700"
          >
            Upload
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="flex-1 py-2 bg-cyan-600 hover:bg-cyan-500 font-bold text-xs font-mono text-white rounded-lg shadow-lg shadow-cyan-600/20"
          >
            Save Avatar
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 text-xs font-mono text-slate-400 hover:text-white"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
